<template>
  <div>
    <warning-notification style="margin-bottom: 0 !important; border-radius: 0 !important;">
      <template #content>
        <p class="mb-0">
          ⚠️ Only ad group, ads of the upgraded smart+ are being showed here.
        </p>
      </template>
    </warning-notification>

    <vue-good-table
      id="list-campaign-table"
      class="vgt-no-border table-campaign"
      mode="remote"
      :columns="columns"
      :rows="adGroups"
      :pagination-options="{
        enabled: totalAdGroup > 0,
        perPage:pageLength,
      }"
      :sort-options="{
        enabled: false
      }"
      :is-loading="loading"
      @on-sort-change="onSortChange"
    >
      <template
        slot-scope="props"
        slot="table-column"
      >
        <span v-if="props.column.field === 'checkbox'">
          <input
            ref="headerCb"
            type="checkbox"
            :checked="isAllChecked"
            @change="onToggleAll($event.target.checked)"
          >
        </span>
        <span v-else>{{ props.column.label }}</span>
      </template>
      <template
        slot-scope="props"
        slot="table-row"
      >
        <span v-if="props.column.field === 'checkbox'">
          <input
            type="checkbox"
            :checked="isRowChecked(props.row)"
            @change="onToggleRow(props.row, $event.target.checked)"
          >
        </span>

        <span v-if="props.column.field === 'toggleStatus'">
          <b-form-checkbox
            :id="props.row.adgroup_id"
            data-testid="toggle-ad-group"
            class="custom-control-success"
            name="check-button"
            switch
            inline
            :checked="isAdGroupActive(props.row)"
            @change="onChangeAdGroupStatus(props.row, $event)"
          />
        </span>

        <span
          v-if="props.column.field === 'adGroupName'"
          class="text-nowrap"
        >
          <p class="mb-0 font-weight-600">
            <b-img
              fluid
              width="18px"
              height="18px"
              :src="require('@/assets/images/icons/ic-smart-plus-plus.svg')"
              alt="campaign smart+"
              style="margin-right: 2px;"
            />
            {{ props.row.adgroup_name }}
          </p>
          <p class="mb-0 text-placeholder">ID: {{ props.row.adgroup_id }}</p>
        </span>

        <div
          v-else-if="props.column.field === 'budget'"
          class="d-flex flex-column"
        >
          <span>{{ props.row.budget_mode === 'BUDGET_MODE_INFINITE' ? 'Use campaign budget' : `${props.row.budget} USD` }}</span>
          <span
            class="font-italic text-gray"
            style="font-size: 12px"
          >{{ getBudgetType(props.row.budget_mode) }}</span>
        </div>

        <div
          v-else-if="props.column.field === 'bid'"
          class="d-flex flex-column text-nowrap"
        >
          <div>
            <p class="mb-0">
              {{ getRoasBidInfo(props.row).value }}
            </p>
            <p class="mb-0 text-placeholder">
              {{ getRoasBidInfo(props.row).type }}
            </p>
          </div>
        </div>

        <div
          v-else-if="props.column.field === 'status'"
          class="d-flex flex-column"
        >
          <span
            v-if="statusAdGroup(props.row)"
            class="campaign-status"
            :class="statusVariant(props.row)"
          >
            {{ statusAdGroup(props.row) }}
          </span>
          <span v-else> N/A </span>
        </div>

        <div
          v-else-if="props.column.field === 'adScheduling'"
          class="d-flex flex-column text-nowrap"
          style="text-align: end"
        >
          <div v-if="props.row.schedule_type === 'SCHEDULE_FROM_NOW'">
            <p class="mb-0">
              {{ createdDate(props.row.schedule_start_time ) }}
            </p>
            <p class="mb-0 text-placeholder">
              Ongoing
            </p>
          </div>
          <div v-if="props.row.schedule_type === 'SCHEDULE_START_END'">
            <p class="mb-0">
              {{ createdDate(props.row.schedule_start_time ) }}
            </p>
            <p class="mb-0">
              {{ createdDate(props.row.schedule_end_time ) }}
            </p>
          </div>
        </div>

        <div
          v-else-if="props.column.field === 'createdAt'"
          class="d-flex flex-column"
        >
          <span>{{ createdDate(props.row.create_time ) }}</span>
        </div>

        <!-- Column: Actions -->
        <div v-else-if="props.column.field === 'actions'">
          <span>
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle mr-25"
                />
              </template>
              <b-dropdown-item @click="editCampaign(props.row)">
                <feather-icon
                  class="mr-50"
                  icon="EditIcon"
                />
                <span>{{ $t('campaign.edit') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click="viewCampaign(props.row)">
                <feather-icon
                  class="mr-50"
                  icon="EyeIcon"
                />
                <span>{{ $t('campaign.view') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </div>

        <!-- Column: common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        v-if="totalAdGroup > 0"
        slot="pagination-bottom"
      >
        <div
          class="d-flex flex-wrap mt-1 pl-2 pb-1 justify-content-between align-items-center"
        >
          <div class="d-flex align-items-center mb-0">
            <span class="text-nowrap">
              Showing 1 to
            </span>
            <b-form-select
              v-model="pageLength"
              :options="pages"
              class="mx-1"
              @input="handlePerPageChange"
            />
            <span class="text-nowrap"> of {{ totalAdGroup }} ad group </span>
          </div>
          <div v-if="totalAdGroup > pageLength">
            <b-pagination
              :total-rows="totalAdGroup"
              :value="currentPage"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mt-md-0 mb-0 pagination-table"
              @change="handleChangePage"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>

      <!-- custom loading  -->
      <template slot="loadingContent">
        <b-spinner
          variant="primary"
          type="grow"
          small
        />
      </template>

      <!-- no account image -->
      <div
        slot="emptystate"
        class="text-center my-1"
      >
        <b-img
          alt="No account"
          class="img"
          :src="require('@/assets/images/common/no-data-table.svg')"
        />
      </div>
      <!-- /no account image -->
    </vue-good-table>
  </div>
</template>
<script>
import {
  BImg, BPagination, BSpinner, BDropdown, BDropdownItem, BFormSelect, BFormCheckbox,
} from 'bootstrap-vue'
import get from 'lodash/get'
import { createNamespacedHelpers } from 'vuex'
import { VueGoodTable } from 'vue-good-table'
import { CAMPAIGN_STATUS } from '@/constants'
import dayjs from 'dayjs'
import campaignMixin from '@/mixins/campaignMixin'
import WarningNotification from '@/views/home/components/WarningNotification.vue'
import { toastification } from '@core/mixins/toast'

const { mapActions, mapGetters } = createNamespacedHelpers('campaign')

export default {
  components: {
    BFormCheckbox,
    BFormSelect,
    WarningNotification,
    BImg,
    BPagination,
    BSpinner,
    BDropdown,
    BDropdownItem,
    VueGoodTable,
  },
  mixins: [campaignMixin, toastification],
  props: {
    advertiserId: {
      type: Object,
      default: () => {
      },
    },

    campaignIds: {
      type: String,
      default: '',
    },

    campaignIdsSelected: {
      type: Array,
      default: () => [],
      require: false,
    },
  },

  data() {
    return {
      adGroups: [],
      adGroupSelected: [],
      rangeDate: {
        startDate: null,
        endDate: null,
      },

      currentPage: 1,

      // sort
      sortTerm: {
        field: '',
        type: '',
      },

      // per page
      pageLength: 10,
      pages: [10, 20, 50],

      columns: [
        {
          label: '',
          field: 'checkbox',
          thClass: 'p-1 border-0 header-table-mf',
          tdClass: 'p-1 border-top-0 border-left-0 border-right-0 border-bottom-1 align-middle',
        },
        {
          label: 'On/off',
          field: 'toggleStatus',
          thClass: 'p-1 border-0 header-table-mf',
          tdClass: 'p-1 border-top-0 border-left-0 border-right-0 border-bottom-1 align-middle',
        },
        {
          label: this.$t('campaign.adGroup.adGroupName'),
          field: 'adGroupName',
          thClass: 'p-1 border-0 header-table-mf',
          tdClass: 'p-1 border-top-0 border-left-0 border-right-0 border-bottom-1 align-middle',
        },
        // {
        //   label: this.$t('campaign.adGroup.status'),
        //   field: 'status',
        //   thClass: 'p-1 border-0 header-table-mf',
        //   tdClass:
        //     'p-1 border-top-0 border-left-0 border-right-0 border-bottom-1 align-middle',
        // },
        {
          label: this.$t('campaign.adGroup.budget'),
          field: 'budget',
          thClass: 'p-1 border-0 header-table-mf',
          tdClass:
            'p-1 border-top-0 border-left-0 border-right-0 border-bottom-1 align-middle',
        },
        {
          label: this.$t('campaign.adGroup.bid'),
          field: 'bid',
          thClass: 'p-1 border-0 header-table-mf',
          tdClass:
            'p-1 border-top-0 border-left-0 border-right-0 border-bottom-1 align-middle',
        },
        {
          label: this.$t('campaign.adGroup.adScheduling'),
          field: 'adScheduling',
          thClass: 'p-1 border-0 header-table-mf',
          tdClass:
            'p-1 border-top-0 border-left-0 border-right-0 border-bottom-1 align-middle',
        },
        {
          label: this.$t('campaign.createdAt'),
          field: 'createdAt',
          thClass: 'p-1 border-0 header-table-mf',
          tdClass:
            'p-1 border-top-0 border-left-0 border-right-0 border-bottom-1 align-middle',
        },
        // {
        //   label: this.$t('campaign.actions'),
        //   field: 'actions',
        //   thClass: 'p-1 border-0 header-table-mf th-column-action',
        //   tdClass:
        //     'p-1 border-top-0 border-left-0 border-right-0 border-bottom-1 align-middle td-column-action',
        // },
      ],
    }
  },

  computed: {
    ...mapGetters(['listAdGroupSpp', 'message', 'status', 'loading']),
    totalAdGroup() {
      return get(this.listAdGroupSpp, ['paging', 'total'], 0)
    },

    createdDate() {
      return value => (value ? dayjs.utc(value).tz(this.advertiserId?.timezone).format('YYYY-MM-DD HH:mm') : '')
    },

    statusAdGroup() {
      const statusAdGroup = {
        [CAMPAIGN_STATUS.DISABLE]: this.$t('campaign.disableStatus'),
        [CAMPAIGN_STATUS.REVIEWING]: this.$t('productLinkPage.statusReviewing'),
        [CAMPAIGN_STATUS.REJECTED]: this.$t('billingPage.statusRejected'),
        [CAMPAIGN_STATUS.APPROVED]: this.$t('myAdAccountsPage.textApproved'),
      }

      return adGroup => statusAdGroup[adGroup && adGroup.operation_status]
    },

    statusVariant() {
      const statusColor = {
        [CAMPAIGN_STATUS.DISABLE]: 'disable',
        [CAMPAIGN_STATUS.REJECTED]: 'rejected',
        [CAMPAIGN_STATUS.REVIEWING]: 'reviewing',
        [CAMPAIGN_STATUS.APPROVED]: 'approved',
      }

      return adGroup => statusColor[adGroup && adGroup.operation_status]
    },

    isAllChecked() {
      if (this.adGroups.length === 0) return false
      return this.adGroups.every(adGroup => this.adGroupSelected.includes(adGroup.adgroup_id))
    },

    isIndeterminate() {
      const selectedInCurrentPage = this.adGroups.filter(adGroup => this.adGroupSelected.includes(adGroup.adgroup_id)).length
      return selectedInCurrentPage > 0 && selectedInCurrentPage < this.adGroups.length
    },
  },

  watch: {
    campaignIdsSelected: {
      handler(val) {
        if (val) {
          this.currentPage = 1
          this.$emit('fetchListAdGroup')
        }
      },
      deep: true,
      immediate: true,
    },

    adGroupSelected: {
      handler() {
        this.$nextTick(() => {
          const cb = this.$refs.headerCb
          if (cb) cb.indeterminate = this.isIndeterminate
        })
      },
      deep: true,
    },

    adGroups() {
      this.$nextTick(() => {
        const cb = this.$refs.headerCb
        if (cb) cb.indeterminate = this.isIndeterminate
      })
    },

    listAdGroupSpp: {
      handler() {
        this.initData()
      },
      deep: true,
    },
  },

  methods: {
    ...mapActions(['getListAdGroupSpp', 'updateStatusAdGroup']),
    initData() {
      this.adGroups = get(this.listAdGroupSpp, ['content'], [])
    },

    getRoasBidInfo(adGroup) {
      if (adGroup.optimization_goal === 'VALUE') {
        return {
          value: adGroup.deep_bid_type === 'VO_MIN_ROAS' ? `${adGroup?.roas_bid}` : '--',
          type: adGroup.deep_bid_type === 'VO_MIN_ROAS' ? 'Minimum ROAS' : 'Highest Value',
        }
      }
      if (adGroup.optimization_goal === 'IN_APP_EVENT') {
        return {
          value: adGroup.bid_type === 'BID_TYPE_CUSTOM' ? `${adGroup?.conversion_bid_price} USD` : '--',
          type: this.convertOptimizationEvent(adGroup.optimization_event),
        }
      }
      if (adGroup.optimization_goal === 'INSTALL') {
        return {
          value: adGroup.bid_type === 'BID_TYPE_CUSTOM' ? `${adGroup.conversion_bid_price} USD` : '--',
          type: 'App Installs',
        }
      }
      if (adGroup.optimization_goal === 'CLICK') {
        return {
          value: adGroup.bid_type === 'BID_TYPE_CUSTOM' ? adGroup.bid_price : '--',
          type: 'Click',
        }
      }
      if (adGroup.optimization_goal === 'LEAD_GENERATION') {
        return {
          value: adGroup.bid_type === 'BID_TYPE_CUSTOM' ? `${adGroup.conversion_bid_price} USD` : '--',
          type: 'Leads',
        }
      }
      if (adGroup.optimization_goal === 'CONVERT') {
        return {
          value: adGroup.bid_type === 'BID_TYPE_CUSTOM' ? `${adGroup.conversion_bid_price} USD` : '--',
          type: this.convertOptimizationEvent(adGroup.optimization_event),
        }
      }
      if (adGroup.optimization_goal === 'TRAFFIC_LANDING_PAGE_VIEW') {
        return {
          value: '--',
          type: 'Landing Page View',
        }
      }
      return {
        value: adGroup?.roas_bid || adGroup.bid_price || adGroup.conversion_bid_price || '--',
        type: this.convertOptimizationEvent(adGroup.optimization_event),
      }
    },

    async onSortChange(params) {
      this.sortTerm.field = params[0].field
      this.sortTerm.type = params[0].type
      await this.fetchListAdGroup()
    },

    async handlePerPageChange(active) {
      this.pageLength = Number(active)
      await this.$emit('fetchListAdGroup', 1, active)
    },

    async handleChangePage(page) {
      await this.$emit('fetchListAdGroup', page, this.pageLength)
    },

    isAdGroupActive(row) {
      return row?.operation_status === 'ENABLE'
    },

    async onChangeAdGroupStatus(row, checked) {
      const originalStatus = row.operation_status

      const params = {
        advertiserId: this.advertiserId.value,
        adgroupIds: [row.adgroup_id],
        operationStatus: checked ? 'ENABLE' : 'DISABLE',
      }

      await this.updateStatusAdGroup(params)
      if (this.status) {
        this.adGroups = this.adGroups.map(adGroup => {
          if (adGroup.adgroup_id === row.adgroup_id) {
            return {
              ...adGroup,
              operation_status: checked ? 'ENABLE' : 'DISABLE',
            }
          }
          return adGroup
        })
        this.toastSuccess('Ad group status updated')
      } else {
        document.getElementById(row.adgroup_id).checked = originalStatus === 'ENABLE'
        this.toastFailure(this.message)
      }
    },

    isRowChecked(row) {
      return this.adGroupSelected.includes(row.adgroup_id)
    },

    onToggleRow(row, checked) {
      if (checked) {
        if (!this.adGroupSelected.includes(row.adgroup_id)) {
          this.adGroupSelected.push(row.adgroup_id)
        }
      } else {
        const index = this.adGroupSelected.indexOf(row.adgroup_id)
        if (index > -1) {
          this.adGroupSelected.splice(index, 1)
        }
      }
      this.$emit('handleSelectAdGroups', this.adGroupSelected)
    },
    onToggleAll(checked) {
      if (checked) {
        // Thêm tất cả adGroups của trang hiện tại vào danh sách đã chọn
        this.adGroups.forEach(adGroup => {
          if (!this.adGroupSelected.includes(adGroup.adgroup_id)) {
            this.adGroupSelected.push(adGroup.adgroup_id)
          }
        })
      } else {
        // Chỉ loại bỏ adGroups của trang hiện tại khỏi danh sách đã chọn
        const currentPageIds = this.adGroups.map(adGroup => adGroup.adgroup_id)
        this.adGroupSelected = this.adGroupSelected.filter(id => !currentPageIds.includes(id))
      }
      this.$emit('handleSelectAdGroups', this.adGroupSelected)
    },

    editCampaign(campaign) {
      this.$emit('editCampaign', campaign)
    },

    viewCampaign(campaign) {
      this.$emit('viewCampaign', campaign)
    },
  },
}
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';

.table-campaign {
  .td-column-id {
    max-width: 200px;
  }

  .td-column-status {
    min-width: 120px;
    width: max-content;
  }

  .td-column-budget {
    min-width: 150px;
    width: max-content;
  }

  .td-column-created-at {
    min-width: 150px;
    width: max-content;
  }

  .th-column-action {
    position: sticky !important;
    z-index: 1;
    right: 0;
  }

  .td-column-action {
    background-color: #fff;
    max-width: 200px;
    width: max-content;
    position: sticky !important;
    z-index: 1;
    right: 0;
  }

  .scroll-action-column {
    &:after {
      content: "";
      position: absolute;
      height: 101%;
      top: 0;
      left: -13px;
      box-shadow: inset 13px 0px 8px -8px #00000026;
    }
  }

  .vgt-responsive {
    overflow-y: clip;
  }
}

.input-control {
  .vs__dropdown-toggle {
    height: 40px;
  }
}

.custom-select-input {
  .vs__dropdown-toggle {
    align-items: center;
  }

  @media (max-width: 767px) {
    .vs__selected-options {
      font-size: 12px
    }

    .vs__dropdown-menu {
      font-size: 12px
    }
  }
}
</style>

<style lang="scss" scoped>
.campaign-status {
  padding: 5px 12px;
  border-radius: 100px;
  width: max-content;

  .content-status {
    margin: 0;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: bold;
  }

  &.disable {
    background: rgba(0, 0, 0, 0.05);
    color: rgba(0, 0, 0, 0.5);
  }

  &.active {
    background: rgba(76, 179, 117, 0.1);
    color: #4CB375;
  }
}

.footer-tab {
  padding: 0 1rem 1rem;
}

.input-height {
  height: 40px;
}

.select-input {
  min-width: 250px;
  width: 250px;

  @media (max-width: 767px) {
    min-width: 150px;
    width: 150px;
  }
}
</style>
